<template>
  <div
      class="app-sidebar-wrapper"
      :class="{'open': isOpen}"
  >
    <div class="app-sidebar-wrapper__overlay"/>
    <nav id="sidebar">
      <div class="sidebar-container">
        <div
            v-if="batlanticUrl"
            class="badlantic">
          <div>
            <a :href="batlanticUrl" target="_blank">
              <img :src="batlanticIcon">
              badlantic
              <FontAwesomeIcon icon="fa-solid fa-arrow-right"/>
            </a>
          </div>
        </div>
        <div class="group-div">
          <SidebarGroup
              v-for="(item, index) in items"
              :key="`sg-1-${index}`"
              :level="1"
              :index="index"
              :item="item"
              :ref="el => { if (el) sidebarGroups[index] = el }"
              @open="closeOther(index)"
          />
        </div>
      </div>
    </nav>
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  ref,
  onBeforeUpdate,
} from 'vue';
import { useStore } from 'vuex';
import SidebarGroup from '@/components/sidebar/SidebarGroup.vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';

library.add(faArrowRight);

defineProps({
  items: {
    type: Array,
    required: true,
  },
});

const store = useStore();
const sidebarGroups = ref<InstanceType<typeof SidebarGroup>[]>([]);
const batlanticIcon = window.ioappConfig.badlanticLogo;
const batlanticUrl = window.ioappConfig.badlanticUrl;

onBeforeUpdate(() => {
  sidebarGroups.value = [];
});

const isOpen = computed(() => store.getters['sidebar/open']);

const closeOther = (keepInx: number) => {
  sidebarGroups.value.forEach((sidebarGroup, groupInx) => {
    if (groupInx !== keepInx) {
      sidebarGroup.close();
    }
  });
};
</script>

<style lang="scss">
@import "src/scss/component-helpers";

.app-sidebar-wrapper {
  &__overlay {
    position: fixed;
    left: 0;
    top: 0;
    width: 100vw;
    height: 100vh;
    background: #293033;
    opacity: 0;
    transition: opacity .475s ease-out;
    z-index: 1000;
    pointer-events: none;
  }

  #sidebar {
    position: fixed;
    right: 0;
    bottom: 0;
    height: 100%;
    transform: translateX(calc(100% + 6px));
    transition: transform .475s ease-out;
    pointer-events: none;
    width: 19.25rem;
    z-index: 1001;
    @include font-size(16px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, .16);
    text-align: right;

    .sidebar-container {
      display: grid;
      grid-template-columns: 1fr;
      grid-auto-rows: auto 1fr auto;
      background-color: #FFFFFF;
      height: 100%;
      @include header-height;

      > div {
        &:first-child {
          grid-row: 1 / 2;
        }

        &:last-child {
          grid-row: 3 / 4;
          padding-bottom: calc(4.6875rem + 13.84615vw);
          overflow: auto;

          .io-sidebar-group:last-child > a {
            border-bottom: 0;
          }
        }
      }
    }

    .badlantic {
      background: $gradient-brand;
      height: 2.5rem;
      text-align: left;

      div {
        padding-top: 0.563rem;
        padding-bottom: 0.5rem;
      }

      &:before{
        content: "";
        display: block;
        width: 0.625rem;
        height: 2.5rem;
        background: transparent linear-gradient(90deg, #000000 0%, #00000000 100%) 0% 0% no-repeat padding-box;
        opacity: 0.1;
        position: absolute;
      }

      a {
        color: #FFFFFF;
        font-weight: 700;
        font-size: 1rem;
        padding: 0.5rem 1.25rem 0.438rem 0;
        text-decoration: none;
      }

      img {
        width: 1rem;
        margin: 0 0.625rem 0 1.25rem;
      }

      svg {
        float: right;
        margin-right: 1.25rem;
        margin-top: 3px;

        path {
          fill: #FFFFFF;
        }
      }
    }
  }

  &.open {
    .app-sidebar-wrapper__overlay {
      opacity: .2;
    }

    #sidebar {
      pointer-events: all;
      transform: translateX(0%);
    }
  }
}
</style>
